import {
  getMessaging, getToken, isSupported, onMessage,
} from 'firebase/messaging'
import { v4 as uuidv4 } from 'uuid'
import Vue from 'vue'
import axios from 'axios'
import { UAParser } from 'ua-parser-js'
import store from '../store/index'
import i18n, { getLocale } from '../i18n'

// Константы для локального хранилища
const STORAGE_KEYS = {
  NOTIFICATION_PERMISSION_COUNT: 'rekassa.kz-tools-notification-permission-request-count',
  WEB_PUSH_UUID: 'rekassa.kz-web-push-uuid',
}

// Константы для максимального количества запросов разрешений
const MAX_PERMISSION_REQUESTS = 2

// API для работы с веб-пуш уведомлениями
const webPushApi = {
  /**
   * Инициализация веб-пуш уведомлений
   */
  async init() {
    // iOS Safari (вне PWA), WebView и старые браузеры не поддерживают Notification/Push API
    const supported = await isSupported().catch(() => false)
    if (!supported) {
      console.debug('FCM(web): Push messaging is not supported in this browser')
      return
    }

    const messaging = getMessaging(store.state.tools.firebaseApp)

    // Обработка входящих сообщений в активном приложении
    this._setupMessageHandler(messaging)

    // Регистрация сервис-воркера
    this._registerServiceWorker(messaging)
  },

  /**
   * Настройка обработчика входящих сообщений
   * @param {Object} messaging - Экземпляр Firebase Messaging
   */
  _setupMessageHandler(messaging) {
    onMessage(messaging, (message) => {
      console.debug('FCM(web): onMessageReceived')
      console.debug(message)

      if (message.data.notification_foreground === 'true' && message.data.notification) {
        this._showForegroundNotification(message)
      }
    })
  },

  /**
   * Отображение уведомления, когда приложение открыто
   * @param {Object} message - Сообщение от Firebase
   */
  _showForegroundNotification(message) {
    const parsedMessage = JSON.parse(message.data.notification)
    const options = {
      body: parsedMessage.body,
      icon: `${import.meta.env.VITE_API_ROOT_URL}/android-chrome-192x192.png`,
      data: {
        url: message.data.url,
      },
    }

    try {
      const notif = new Notification(parsedMessage.title, options)

      notif.onclick = (event) => {
        if (event.currentTarget.data.url) {
          Vue.prototype.$openLink(event.currentTarget.data.url)
        }
        notif.close()
      }
    } catch (error) {
      // Android Chrome запрещает конструктор Notification со страницы —
      // показываем через сервис-воркер, клик обработает notificationclick в firebase-messaging-sw.js
      navigator.serviceWorker.ready.then((registration) => registration.showNotification(parsedMessage.title, options))
    }
  },

  /**
   * Регистрация сервис-воркера
   * @param {Object} messaging - Экземпляр Firebase Messaging
   */
  _registerServiceWorker(messaging) {
    if (!('serviceWorker' in navigator)) {
      return
    }

    const swFileName = import.meta.env.VITE_TEST === 'true'
      ? '/firebase-messaging-sw-test.js'
      : '/firebase-messaging-sw.js'

    navigator.serviceWorker.register(swFileName).then(
      (registration) => this._handleServiceWorkerRegistration(messaging, registration),
      (error) => console.error(`FCM(web): Service worker registration failed: ${error}`),
    )
  },

  /**
   * Обработка успешной регистрации сервис-воркера
   * @param {Object} messaging - Экземпляр Firebase Messaging
   * @param {Object} registration - Результат регистрации сервис-воркера
   */
  _handleServiceWorkerRegistration(messaging, registration) {
    const notificationPermissionRequestCount = parseInt(
      localStorage.getItem(STORAGE_KEYS.NOTIFICATION_PERMISSION_COUNT) || '0',
      10,
    )

    if (Notification.permission === 'granted') {
      console.debug('FCM(web): Notifications permission was granted previously')
      this.requestToken(messaging, registration)
    } else if (notificationPermissionRequestCount < MAX_PERMISSION_REQUESTS) {
      this._askForNotificationPermission(messaging, registration, notificationPermissionRequestCount)
    }
  },

  /**
   * Запрос разрешения на уведомления
   * @param {Object} messaging - Экземпляр Firebase Messaging
   * @param {Object} registration - Результат регистрации сервис-воркера
   * @param {Number} currentCount - Текущее количество запросов
   */
  _askForNotificationPermission(messaging, registration, currentCount) {
    localStorage.setItem(STORAGE_KEYS.NOTIFICATION_PERMISSION_COUNT, currentCount + 1)

    setTimeout(() => {
      store.dispatch('confirm/showConfirm', {
        title: i18n.t('разрешите_уведомления_чтобы_не_пропустить_информацию_о_налогах_отчетах_и_полезных_обновлениях'),
        resolveText: `${i18n.t('разрешить')}`,
        rejectText: i18n.t('не_сейчас'),
      })
        .then(() => this._requestNotificationPermission(messaging, registration))
        .catch(() => {
          store.dispatch('analytics/logEvent', { eventName: 're_push_notifications_rejected' })
        })
    }, 4000)
  },

  /**
   * Запрос разрешения на уведомления через API браузера
   * @param {Object} messaging - Экземпляр Firebase Messaging
   * @param {Object} registration - Результат регистрации сервис-воркера
   */
  _requestNotificationPermission(messaging, registration) {
    Notification.requestPermission().then((permission) => {
      console.debug(`FCM(web): Notifications permission was ${permission === 'granted' ? 'granted' : 'denied'}`)

      if (permission === 'granted') {
        store.dispatch('analytics/logEvent', { eventName: 're_push_notifications_approved' })
        this.requestToken(messaging, registration)
      } else {
        store.dispatch('analytics/logEvent', { eventName: 're_push_notifications_rejected' })
      }
    })
  },

  /**
   * Запрос токена для веб-пуш уведомлений
   * @param {Object} messaging - Экземпляр Firebase Messaging
   * @param {Object} registration - Результат регистрации сервис-воркера
   */
  requestToken(messaging, registration) {
    getToken(messaging, {
      vapidKey: import.meta.env.VITE_PUSH_VAPID_PUBLIC_KEY,
      serviceWorkerRegistration: registration,
    })
      .then((token) => {
        if (token) {
          console.debug('FCM(web): getToken: ', token)
          this.subscribe(token)
        } else {
          console.debug('FCM(web): No registration token available. Request permission to generate one.')
        }
      })
      .catch((err) => {
        console.debug('FCM(web): An error occurred while retrieving token. ', err)
      })
  },

  /**
   * Подписка на уведомления с использованием полученного токена
   * @param {String} token - Токен Firebase для уведомлений
   */
  async subscribe(token) {
    try {
      const uuid = localStorage.getItem(STORAGE_KEYS.WEB_PUSH_UUID) || uuidv4()
      localStorage.setItem(STORAGE_KEYS.WEB_PUSH_UUID, uuid)

      const { bearerToken, userUid } = await store.dispatch('auth/getBearerToken')
      const config = {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          'x-user-uid': userUid,
        },
      }

      const baseUrl = import.meta.env.VITE_API_ROOT_URL || ''
      const url = `${baseUrl}/notification/users/tokens`

      await axios.post(
        url,
        {
          deviceId: uuid,
          token,
          lang: getLocale().toUpperCase(),
          detailedInfo: this.getDetailedInfo(),
          deviceType: 'WEB',
        },
        config,
      )

      console.debug(`FCM(device): Subscribed for push notifications deviceId: ${uuid}`)
    } catch (error) {
      console.debug('FCM(device): Failed to subscribe: ', error)
    }
  },

  /**
   * Отписка от уведомлений
   * @returns {Promise} Promise с результатом операции
   */
  async unsubscribe() {
    try {
      const uuid = localStorage.getItem(STORAGE_KEYS.WEB_PUSH_UUID)
      if (!uuid) {
        return
      }

      const { bearerToken, userUid } = await store.dispatch('auth/getBearerToken')
      const config = {
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          'x-user-uid': userUid,
        },
      }

      const baseUrl = import.meta.env.VITE_API_ROOT_URL || ''
      const url = `${baseUrl}/notification/users/device-ids/${uuid}`

      await axios.delete(url, config)
      console.debug(`FCM(device): Unsubscribed for push notifications deviceId: ${uuid}`)
    } catch (error) {
      console.debug('FCM(device): Failed to unsubscribe: ', error)
    }
  },

  /**
   * Получение детальной информации о устройстве пользователя
   * @returns {Object} Объект с информацией о пользовательском агенте
   */
  getDetailedInfo() {
    const info = {}
    try {
      info.userAgent = UAParser(navigator.userAgent)
    } catch (error) {
      console.debug(error)
    }
    return info
  },
}

export default webPushApi
